/**
 * ImageEditorCanvas Component
 *
 * Fabric.js Canvas für Masken-Zeichnung (DIRECT implementation like FabricTest)
 * @accessibility WCAG 2.1 AA konform
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as fabric from 'fabric';
import { ImageEditorToolbar } from './ImageEditorToolbar';
import type { CanvasTool } from '@/hooks/useCanvas';

interface ImageEditorCanvasProps {
  imageUrl: string | null;
  onMaskChange: (mask: Blob | null) => void;
  selectedTask: 'inpaint_add' | 'inpaint_remove' | 'outpaint' | 'background_edit';
  targetAspectRatio: string;
  setTargetAspectRatio: (ratio: string) => void;
}

export function ImageEditorCanvas({
  imageUrl,
  onMaskChange,
  selectedTask,
  targetAspectRatio,
  setTargetAspectRatio,
}: ImageEditorCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricCanvasRef = useRef<fabric.Canvas | null>(null);

  const { t } = useTranslation();
  // Outpaint mode state
  const [imageAlignment, setImageAlignment] = useState<'center' | 'left' | 'right' | 'top' | 'middle' | 'bottom'>('center');

  // Helper: Calculate aspect ratio from dimensions
  const calculateAspectRatio = (width: number, height: number): string => {
    const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
    const divisor = gcd(width, height);
    const w = width / divisor;
    const h = height / divisor;

    // Map to common ratios
    const ratio = w / h;
    if (Math.abs(ratio - 1) < 0.01) return '1:1';
    if (Math.abs(ratio - 4/3) < 0.01) return '4:3';
    if (Math.abs(ratio - 16/9) < 0.01) return '16:9';
    if (Math.abs(ratio - 9/16) < 0.01) return '9:16';
    if (Math.abs(ratio - 21/9) < 0.01) return '21:9';

    return `${Math.round(w)}:${Math.round(h)}`;
  };

  // Export outpaint mask (inverse: white where no image)
  const exportOutpaintMask = useCallback(() => {
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    const objects = canvas.getObjects();
    const image = objects.find(obj => obj.type === 'image');

    if (!image) {
      console.warn('⚠️ No image found for outpaint mask generation');
      return;
    }

    // Create mask canvas
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = canvas.width!;
    maskCanvas.height = canvas.height!;
    const ctx = maskCanvas.getContext('2d');
    if (!ctx) return;

    // Fill entire canvas with WHITE (= outpaint these areas)
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);

    // Fill image area with BLACK (= keep this area unchanged)
    const imgLeft = image.left || 0;
    const imgTop = image.top || 0;
    const imgWidth = (image.width || 0) * (image.scaleX || 1);
    const imgHeight = (image.height || 0) * (image.scaleY || 1);

    ctx.fillStyle = '#000000';
    ctx.fillRect(imgLeft, imgTop, imgWidth, imgHeight);

    console.log(`🎨 Outpaint mask: white canvas (${maskCanvas.width}x${maskCanvas.height}), black image area (${imgWidth}x${imgHeight} at ${imgLeft},${imgTop})`);

    // Export as blob
    maskCanvas.toBlob((blob) => {
      if (blob) {
        console.log('✅ Outpaint mask exported:', blob.size, 'bytes');
        onMaskChange(blob);
      }
    }, 'image/png');
  }, [onMaskChange]);

  // State for toolbar
  const [currentTool, setCurrentTool] = useState<CanvasTool>('brush');
  const [brushSize, setBrushSize] = useState(20);

  // Check if in outpaint mode
  const isOutpaintMode = selectedTask === 'outpaint';

  // Ref to track current tool (for event handlers in closure)
  const currentToolRef = useRef<CanvasTool>('brush');

  // State to track canvas initialization (for React Strict Mode!)
  const [canvasInitialized, setCanvasInitialized] = useState(false);
  const [triggerMaskExport, setTriggerMaskExport] = useState(0);

  // Initialize Fabric Canvas (like FabricTest!)
  useEffect(() => {
    if (!canvasRef.current) return;

    console.log('🧪 DIRECT: Initializing Fabric.js...');

    const canvas = new fabric.Canvas(canvasRef.current, {
      width: 800,
      height: 600,
      backgroundColor: isOutpaintMode ? 'transparent' : '#f5f5f5',
      isDrawingMode: false, // Start with selection mode
    });

    fabricCanvasRef.current = canvas;
    console.log('✅ Canvas created:', canvas);

    // Configure brush
    if (canvas.freeDrawingBrush) {
      canvas.freeDrawingBrush.color = 'rgba(255, 128, 0, 0.3)'; // 70% transparent Orange
      canvas.freeDrawingBrush.width = brushSize;
    }

    // Helper: Export pure black/white mask (no image)
    const exportMask = () => {
      if (!canvas) return;

      // Create temporary canvas for mask
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = canvas.width!;
      maskCanvas.height = canvas.height!;
      const ctx = maskCanvas.getContext('2d');
      if (!ctx) return;

      // Fill with black background (unmasked area)
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);

      // Draw all paths and rectangles in white (masked area)
      const objects = canvas.getObjects();
      const paths = objects.filter(o => o.type === 'path');
      const rects = objects.filter(o => o.type === 'rect');
      const eraserPaths = paths.filter((p: fabric.FabricObject & { eraserPath?: boolean }) => p.eraserPath);
      const brushPaths = paths.filter((p: fabric.FabricObject & { eraserPath?: boolean }) => !p.eraserPath);
      console.log(`🎨 Exporting mask: ${brushPaths.length} brush paths, ${eraserPaths.length} eraser paths, ${rects.length} rectangles`);

      objects.forEach((obj: fabric.FabricObject & { eraserPath?: boolean; path?: (string | number)[][] }) => {
        // Skip the background image (only draw paths and rectangles)
        if (obj.type === 'path') {
          ctx.save();
          const lineWidth = obj.strokeWidth || brushSize;

          // Eraser paths draw black (remove from mask)
          if (obj.eraserPath) {
            console.log(`  Eraser path: ${lineWidth}px (painting black)`);
            ctx.strokeStyle = '#000000'; // Paint black to remove from mask
          } else {
            console.log(`  Brush path: ${obj.strokeWidth || 'undefined'} → using ${lineWidth}px`);
            ctx.strokeStyle = '#FFFFFF'; // Paint white to add to mask
          }

          ctx.lineWidth = lineWidth;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';

          // Draw the path
          const path = obj.path;
          if (path && path.length > 0) {
            ctx.beginPath();
            for (let i = 0; i < path.length; i++) {
              const point = path[i];
              if (point[0] === 'M') {
                ctx.moveTo(point[1] as number, point[2] as number);
              } else if (point[0] === 'Q') {
                ctx.quadraticCurveTo(point[1] as number, point[2] as number, point[3] as number, point[4] as number);
              } else if (point[0] === 'L') {
                ctx.lineTo(point[1] as number, point[2] as number);
              }
            }
            ctx.stroke();
          }
          ctx.restore();
        } else if (obj.type === 'rect') {
          // Draw rectangle as filled white area
          ctx.save();
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(obj.left, obj.top, obj.width * obj.scaleX, obj.height * obj.scaleY);
          console.log(`  Rectangle: ${obj.width}x${obj.height} at (${obj.left}, ${obj.top})`);
          ctx.restore();
        }
      });

      // Export as blob
      maskCanvas.toBlob((blob) => {
        if (blob) {
          console.log('✅ Pure mask exported:', blob.size, 'bytes');
          onMaskChange(blob);
        }
      }, 'image/png');
    };

    // Make drawn paths non-selectable AND export mask
    canvas.on('path:created', (e: { path: fabric.FabricObject & { eraserPath?: boolean } }) => {
      if (e.path) {
        e.path.selectable = false;
        e.path.evented = false;

        // Mark eraser paths with custom property (use ref to avoid closure issue)
        const activeTool = currentToolRef.current;
        console.log(`🎨 Path created with tool: ${activeTool}`);
        if (activeTool === 'eraser') {
          e.path.eraserPath = true;
          console.log('🧹 Eraser path marked as eraserPath = true');
        } else {
          console.log('✏️ Brush path (no eraserPath flag)');
        }
      }

      // Export pure black/white mask (no image)
      console.log('🎨 Path created, exporting mask...');
      exportMask();
    });

    // Trigger image reload (for React Strict Mode!)
    setCanvasInitialized(prev => !prev);

    return () => {
      console.log('🧹 Cleanup: Disposing canvas');
      canvas.dispose();
      fabricCanvasRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- canvas init runs once
  }, []);

  // Load image when URL changes OR canvas is re-initialized (React Strict Mode!)
  useEffect(() => {
    if (!imageUrl || !fabricCanvasRef.current) return;

    console.log('🖼️ DIRECT: Loading image:', imageUrl);

    const canvas = fabricCanvasRef.current;

    // Convert Blob URL to DataURL (like FabricTest!)
    fetch(imageUrl)
      .then(response => response.blob())
      .then(blob => {
        const reader = new FileReader();
        reader.onload = (event) => {
          const imgElement = new Image();
          imgElement.onload = () => {
            console.log('✅ Image loaded:', imgElement.width, 'x', imgElement.height);

            // Auto-detect aspect ratio in outpaint mode
            if (isOutpaintMode) {
              const detectedRatio = calculateAspectRatio(imgElement.width, imgElement.height);
              console.log(`📐 Auto-detected ratio: ${detectedRatio}`);
              setTargetAspectRatio(detectedRatio); // Set as default target
            }

            const fabricImg = new fabric.Image(imgElement);

            // WICHTIG: IMMER auf max. 700px Breite skalieren (für beide Modi!)
            const MAX_WIDTH = 700;
            const scale = imgElement.width > MAX_WIDTH ? MAX_WIDTH / imgElement.width : 1;
            const scaledWidth = imgElement.width * scale;
            const scaledHeight = imgElement.height * scale;

            console.log(`📏 Scaling: ${imgElement.width}x${imgElement.height} → ${scaledWidth}x${scaledHeight} (scale: ${scale})`);

            // Outpaint Mode: Image is draggable, scalable and selectable
            if (isOutpaintMode) {
              // Set canvas to SCALED image size initially
              canvas.setDimensions({ width: scaledWidth, height: scaledHeight });

              fabricImg.set({
                scaleX: scale,  // Scale to max 800px width
                scaleY: scale,
                left: 0,
                top: 0,
                originX: 'left',
                originY: 'top',
                selectable: true,  // Draggable + Scalable
                evented: true,
                hasControls: true, // Enable resize/scale controls
                hasBorders: true,
                borderColor: '#FF6B00',
                borderDashArray: [5, 5],
                cornerColor: '#FF6B00',
                cornerStyle: 'circle',
                transparentCorners: false,
              });
            } else {
              // Masking Mode: Image is static background (full canvas)
              canvas.setDimensions({ width: scaledWidth, height: scaledHeight });

              fabricImg.set({
                scaleX: scale,
                scaleY: scale,
                left: 0,
                top: 0,
                originX: 'left',
                originY: 'top',
                selectable: false,
                evented: false,
                lockMovementX: true,
                lockMovementY: true,
              });
            }

            // Clear and add image
            canvas.clear();
            // Outpaint: Transparent (CSS checkboard pattern shows through)
            // Masking: Gray background
            canvas.backgroundColor = isOutpaintMode ? 'transparent' : '#f5f5f5';
            canvas.add(fabricImg);
            // Das Bild ist automatisch im Hintergrund (erstes Objekt nach clear)

            canvas.renderAll();

            console.log('🎉 Image added successfully!');

            // Auto-export mask in outpaint mode
            if (isOutpaintMode) {
              setTimeout(() => exportOutpaintMask(), 100);
            }
          };
          imgElement.src = event.target?.result as string;
        };
        reader.readAsDataURL(blob);
      })
      .catch(err => {
        console.error('❌ Failed to load image:', err);
      });
  }, [imageUrl, canvasInitialized, isOutpaintMode, exportOutpaintMask, setTargetAspectRatio]);

  // Outpaint: Re-export mask when image is manually moved
  useEffect(() => {
    if (!isOutpaintMode || !fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    const handleObjectModified = () => {
      console.log('🖼️ Image position changed, re-exporting mask');
      exportOutpaintMask();
    };

    canvas.on('object:modified', handleObjectModified);

    return () => {
      canvas.off('object:modified', handleObjectModified);
    };
  }, [isOutpaintMode, exportOutpaintMask]); // ← WICHTIG: canvasInitialized als Dependency!

  // Outpaint: Align image when alignment changes
  useEffect(() => {
    if (!isOutpaintMode || !fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;
    const objects = canvas.getObjects();
    const image = objects.find(obj => obj.type === 'image');

    if (!image) return;

    const canvasWidth = canvas.width!;
    const canvasHeight = canvas.height!;
    const imgWidth = (image.width || 0) * (image.scaleX || 1);
    const imgHeight = (image.height || 0) * (image.scaleY || 1);

    let newLeft = image.left;
    let newTop = image.top;

    // Horizontal alignment
    if (imageAlignment === 'left') {
      newLeft = 0;
    } else if (imageAlignment === 'center') {
      newLeft = (canvasWidth - imgWidth) / 2;
      newTop = (canvasHeight - imgHeight) / 2;
    } else if (imageAlignment === 'right') {
      newLeft = canvasWidth - imgWidth;
    }

    // Vertical alignment
    if (imageAlignment === 'top') {
      newTop = 0;
    } else if (imageAlignment === 'middle') {
      newTop = (canvasHeight - imgHeight) / 2;
    } else if (imageAlignment === 'bottom') {
      newTop = canvasHeight - imgHeight;
    }

    image.set({ left: newLeft, top: newTop });
    canvas.renderAll();

    console.log(`📐 Image aligned: ${imageAlignment}`);

    // Auto-export mask after alignment
    exportOutpaintMask();
  }, [imageAlignment, isOutpaintMode, exportOutpaintMask]);

  // Outpaint: Adjust canvas size when aspect ratio changes
  useEffect(() => {
    if (!isOutpaintMode || !fabricCanvasRef.current || !imageUrl) return;

    const canvas = fabricCanvasRef.current;
    const objects = canvas.getObjects();
    const image = objects.find(obj => obj.type === 'image');

    if (!image) return;

    const MAX_SIZE = 700;
    const [widthRatio, heightRatio] = targetAspectRatio.split(':').map(Number);

    // 1. Calculate canvas size from target ratio (max 700px)
    let newCanvasWidth, newCanvasHeight;

    if (widthRatio > heightRatio) {
      // Landscape: max width
      newCanvasWidth = MAX_SIZE;
      newCanvasHeight = Math.round(MAX_SIZE * (heightRatio / widthRatio));
    } else if (heightRatio > widthRatio) {
      // Portrait: max height
      newCanvasHeight = MAX_SIZE;
      newCanvasWidth = Math.round(MAX_SIZE * (widthRatio / heightRatio));
    } else {
      // Square
      newCanvasWidth = newCanvasHeight = MAX_SIZE;
    }

    // 2. Scale image to fit inside canvas (keep aspect ratio, fully visible)
    const imageAspect = (image.width || 1) / (image.height || 1);
    const canvasAspect = newCanvasWidth / newCanvasHeight;

    let newImageScale;
    if (canvasAspect > imageAspect) {
      // Canvas is wider than image → fit to height
      newImageScale = newCanvasHeight / (image.height || 1);
    } else {
      // Canvas is taller than image → fit to width
      newImageScale = newCanvasWidth / (image.width || 1);
    }

    // 3. Calculate final image dimensions
    const finalImageWidth = (image.width || 0) * newImageScale;
    const finalImageHeight = (image.height || 0) * newImageScale;

    console.log(`🎞️ Canvas resized to ${newCanvasWidth}x${newCanvasHeight} (${targetAspectRatio})`);
    console.log(`  Image scaled: ${finalImageWidth.toFixed(0)}x${finalImageHeight.toFixed(0)} (scale: ${newImageScale.toFixed(3)})`);

    // 4. Update canvas and image
    canvas.setDimensions({ width: newCanvasWidth, height: newCanvasHeight });

    image.set({
      scaleX: newImageScale,
      scaleY: newImageScale,
      left: (newCanvasWidth - finalImageWidth) / 2,
      top: (newCanvasHeight - finalImageHeight) / 2,
    });

    canvas.renderAll();

    // Auto-export mask after ratio change
    exportOutpaintMask();
  }, [targetAspectRatio, isOutpaintMode, imageUrl, exportOutpaintMask]);

  // Rectangle tool event handlers
  useEffect(() => {
    if (!fabricCanvasRef.current || currentTool !== 'rectangle') return;

    const canvas = fabricCanvasRef.current;
    let isDrawingRect = false;
    let rectStartPoint: { x: number; y: number } | null = null;
    let currentRect: fabric.Rect | null = null;

    const handleMouseDown = (e: fabric.TEvent) => {
      isDrawingRect = true;
      const pointer = canvas.getScenePoint(e.e);
      rectStartPoint = { x: pointer.x, y: pointer.y };

      // Create rectangle with orange semi-transparent fill
      currentRect = new fabric.Rect({
        left: pointer.x,
        top: pointer.y,
        width: 0,
        height: 0,
        fill: 'rgba(255, 128, 0, 0.3)',
        stroke: 'rgba(255, 128, 0, 0.8)',
        strokeWidth: 2,
        selectable: false,
        evented: false,
      });

      canvas.add(currentRect);
    };

    const handleMouseMove = (e: fabric.TEvent) => {
      if (!isDrawingRect || !currentRect || !rectStartPoint) return;

      const pointer = canvas.getScenePoint(e.e);
      const width = pointer.x - rectStartPoint.x;
      const height = pointer.y - rectStartPoint.y;

      // Update rectangle size
      if (width < 0) {
        currentRect.set({ left: pointer.x });
      }
      if (height < 0) {
        currentRect.set({ top: pointer.y });
      }
      currentRect.set({
        width: Math.abs(width),
        height: Math.abs(height),
      });

      canvas.renderAll();
    };

    const handleMouseUp = () => {
      if (!isDrawingRect) return;

      isDrawingRect = false;
      rectStartPoint = null;
      currentRect = null;

      // Export mask after rectangle is created
      console.log('🎨 Rectangle created, triggering mask export');
      setTriggerMaskExport(prev => prev + 1); // Trigger mask export
    };

    canvas.on('mouse:down', handleMouseDown);
    canvas.on('mouse:move', handleMouseMove);
    canvas.on('mouse:up', handleMouseUp);

    console.log('✅ Rectangle event handlers registered');

    return () => {
      canvas.off('mouse:down', handleMouseDown);
      canvas.off('mouse:move', handleMouseMove);
      canvas.off('mouse:up', handleMouseUp);
      console.log('🧹 Rectangle event handlers removed');
    };
  }, [currentTool, onMaskChange]);

  // Export mask when triggered (e.g., after rectangle creation)
  useEffect(() => {
    if (!fabricCanvasRef.current || triggerMaskExport === 0) return;

    const canvas = fabricCanvasRef.current;

    // Create temporary canvas for mask
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = canvas.width!;
    maskCanvas.height = canvas.height!;
    const ctx = maskCanvas.getContext('2d');
    if (!ctx) return;

    // Fill with black background (unmasked area)
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);

    // Draw all paths and rectangles in white (masked area)
    const objects = canvas.getObjects();
    const paths = objects.filter(o => o.type === 'path');
    const rects = objects.filter(o => o.type === 'rect');
    console.log(`🎨 [Trigger] Exporting mask with ${paths.length} paths and ${rects.length} rectangles`);

    objects.forEach((obj: fabric.FabricObject & { eraserPath?: boolean; path?: (string | number)[][] }) => {
      if (obj.type === 'path') {
        ctx.save();
        const lineWidth = obj.strokeWidth || brushSize;

        // Eraser paths draw black (remove from mask)
        if (obj.eraserPath) {
          ctx.strokeStyle = '#000000'; // Paint black to remove from mask
        } else {
          ctx.strokeStyle = '#FFFFFF'; // Paint white to add to mask
        }

        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        const path = obj.path;
        if (path && path.length > 0) {
          ctx.beginPath();
          for (let i = 0; i < path.length; i++) {
            const point = path[i];
            if (point[0] === 'M') {
              ctx.moveTo(point[1] as number, point[2] as number);
            } else if (point[0] === 'Q') {
              ctx.quadraticCurveTo(point[1] as number, point[2] as number, point[3] as number, point[4] as number);
            } else if (point[0] === 'L') {
              ctx.lineTo(point[1] as number, point[2] as number);
            }
          }
          ctx.stroke();
        }
        ctx.restore();
      } else if (obj.type === 'rect') {
        ctx.save();
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(obj.left, obj.top, obj.width * obj.scaleX, obj.height * obj.scaleY);
        ctx.restore();
      }
    });

    // Export as blob
    maskCanvas.toBlob((blob) => {
      if (blob) {
        console.log('✅ [Trigger] Pure mask exported:', blob.size, 'bytes');
        onMaskChange(blob);
      }
    }, 'image/png');
  }, [triggerMaskExport, brushSize, onMaskChange]);

  // Update ref when tool changes
  useEffect(() => {
    currentToolRef.current = currentTool;
  }, [currentTool]);

  // Tool handlers
  const handleToolChange = (tool: CanvasTool) => {
    setCurrentTool(tool);
    if (!fabricCanvasRef.current) return;

    const canvas = fabricCanvasRef.current;

    console.log('🎨 Tool changed to:', tool);

    if (tool === 'brush') {
      // Brush explizit erstellen falls nicht vorhanden
      if (!canvas.freeDrawingBrush) {
        canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
        console.log('✨ Created PencilBrush');
      }

      canvas.isDrawingMode = true;
      canvas.freeDrawingBrush.color = 'rgba(255, 128, 0, 0.3)'; // 70% transparent, immer!
      canvas.freeDrawingBrush.width = brushSize;

      console.log('✅ Brush activated:', {
        isDrawingMode: canvas.isDrawingMode,
        color: canvas.freeDrawingBrush.color,
        width: canvas.freeDrawingBrush.width
      });
    } else if (tool === 'eraser') {
      // Brush explizit erstellen falls nicht vorhanden
      if (!canvas.freeDrawingBrush) {
        canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
        console.log('✨ Created PencilBrush');
      }

      canvas.isDrawingMode = true;
      canvas.freeDrawingBrush.color = 'rgba(255, 255, 255, 0.8)';
      canvas.freeDrawingBrush.width = brushSize;

      console.log('✅ Eraser activated:', {
        isDrawingMode: canvas.isDrawingMode,
        color: canvas.freeDrawingBrush.color,
        width: canvas.freeDrawingBrush.width
      });
    } else if (tool === 'rectangle') {
      canvas.isDrawingMode = false;
      console.log('✅ Rectangle tool activated (event handlers managed in useEffect)');
    } else if (tool === 'magic') {
      canvas.isDrawingMode = false;
      console.log('Magic wand - coming soon!');
    } else {
      canvas.isDrawingMode = false;
    }
  };

  const handleBrushSizeChange = (size: number) => {
    setBrushSize(size);
    if (fabricCanvasRef.current?.freeDrawingBrush) {
      fabricCanvasRef.current.freeDrawingBrush.width = size;
    }
  };

  const handleClear = () => {
    if (!fabricCanvasRef.current || !imageUrl) return;

    // Clear all paths AND rectangles (keep only background image)
    const objects = fabricCanvasRef.current.getObjects();
    const objectsToRemove = objects.filter(obj => obj.type === 'path' || obj.type === 'rect');
    objectsToRemove.forEach(obj => fabricCanvasRef.current?.remove(obj));

    fabricCanvasRef.current.renderAll();
    onMaskChange(null); // Clear mask
    console.log('🗑️ Cleared all mask objects');
  };

  const handleUndo = () => {
    if (!fabricCanvasRef.current) return;
    const objects = fabricCanvasRef.current.getObjects();

    // Find last drawn object (path or rectangle, skip background image)
    const drawnObjects = objects.filter(obj => obj.type === 'path' || obj.type === 'rect');
    if (drawnObjects.length > 0) {
      const lastObject = drawnObjects[drawnObjects.length - 1];
      fabricCanvasRef.current.remove(lastObject);
      fabricCanvasRef.current.renderAll();
      console.log('↩️ Undo: removed last object');

      // Re-export mask
      if (drawnObjects.length > 1) {
        setTriggerMaskExport(prev => prev + 1); // Trigger mask re-export
      } else {
        onMaskChange(null); // No objects left, clear mask
      }
    }
  };

  return (
    <div className="image-editor-canvas flex flex-col h-full bg-neutral-50 dark:bg-neutral-900">
      {/* Outpaint Controls (only in outpaint mode) */}
      {isOutpaintMode && (
        <div className="flex items-center gap-4 p-4 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
          {/* Aspect Ratio Selector */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              {t('editor.aspectRatio')}
            </label>
            <select
              value={targetAspectRatio}
              onChange={(e) => setTargetAspectRatio(e.target.value)}
              className="px-3 py-1 text-sm border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-700"
            >
              <option value="1:1">1:1 (Quadrat)</option>
              <option value="4:3">4:3</option>
              <option value="16:9">16:9 (Landscape)</option>
              <option value="9:16">9:16 (Portrait)</option>
              <option value="21:9">21:9 (Ultra-Wide)</option>
            </select>
          </div>

          {/* Alignment Buttons */}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Ausrichtung:</span>
            <div className="flex gap-1">
              <button onClick={() => setImageAlignment('left')} className="px-2 py-1 text-xs border rounded hover:bg-neutral-100 dark:hover:bg-neutral-700" title="Links">◀</button>
              <button onClick={() => setImageAlignment('center')} className="px-2 py-1 text-xs border rounded hover:bg-neutral-100 dark:hover:bg-neutral-700" title="Zentriert">●</button>
              <button onClick={() => setImageAlignment('right')} className="px-2 py-1 text-xs border rounded hover:bg-neutral-100 dark:hover:bg-neutral-700" title="Rechts">▶</button>
              <div className="w-px h-6 bg-neutral-300 dark:bg-neutral-600 mx-1"></div>
              <button onClick={() => setImageAlignment('top')} className="px-2 py-1 text-xs border rounded hover:bg-neutral-100 dark:hover:bg-neutral-700" title="Oben">▲</button>
              <button onClick={() => setImageAlignment('middle')} className="px-2 py-1 text-xs border rounded hover:bg-neutral-100 dark:hover:bg-neutral-700" title="Mittig">●</button>
              <button onClick={() => setImageAlignment('bottom')} className="px-2 py-1 text-xs border rounded hover:bg-neutral-100 dark:hover:bg-neutral-700" title="Unten">▼</button>
            </div>
          </div>
        </div>
      )}

      {/* Canvas Area */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-auto">
        <div className="relative">
          {!imageUrl && (
            <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
              <div className="text-center">
                <p className="text-neutral-500 dark:text-neutral-400 text-lg font-medium">
                  {t('editor.selectImage')}
                </p>
                <p className="text-neutral-400 dark:text-neutral-500 text-sm mt-2">
                  {isOutpaintMode ? t('editor.uploadForOutpaint') : t('editor.uploadImage')}
                </p>
              </div>
            </div>
          )}
          <canvas
            ref={canvasRef}
            style={{
              border: '2px solid black',
              display: 'block',
              ...(isOutpaintMode && {
                backgroundImage: `
                  linear-gradient(45deg, #e0e0e0 25%, transparent 25%),
                  linear-gradient(-45deg, #e0e0e0 25%, transparent 25%),
                  linear-gradient(45deg, transparent 75%, #e0e0e0 75%),
                  linear-gradient(-45deg, transparent 75%, #e0e0e0 75%)
                `,
                backgroundSize: '16px 16px',
                backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0px',
                backgroundColor: '#f5f5f5',
              })
            }}
            aria-label="Bildbearbeitungs-Canvas"
          />
        </div>
      </div>

      {/* Toolbar (only in masking modes) */}
      {!isOutpaintMode && (
        <ImageEditorToolbar
          selectedTool={currentTool}
          onToolChange={handleToolChange}
          brushSize={brushSize}
          onBrushSizeChange={handleBrushSizeChange}
          onUndo={handleUndo}
          onClear={handleClear}
          disabled={!imageUrl}
        />
      )}
    </div>
  );
}
